import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";

export default function Education() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/education`);
      setResources(response.data);
    } catch (error) {
      console.error("Error fetching resources:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["All", "Budgeting", "Savings", "Credit", "Debt", "Investing", "Retirement", "Housing", "Business", "Family"];

  const filteredResources = selectedCategory === "All"
    ? resources
    : resources.filter((r) => r.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[#1B3A5F] text-lg">Loading resources...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8 animate-fadeIn">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
            Financial Education Center
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Comprehensive, practical financial knowledge to empower your journey toward lasting independence. 
            Each resource is designed to provide actionable insights you can apply immediately.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8" data-testid="category-filter">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              data-testid={`category-${category.toLowerCase()}`}
              className={`px-5 py-2 rounded-full font-medium transition-colors ${
                selectedCategory === category
                  ? "bg-[#1B3A5F] text-white"
                  : "bg-white border border-slate-200 text-gray-700 hover:bg-slate-50"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
          {filteredResources.map((resource) => (
            <Link
              key={resource.id}
              to={`/education/${resource.id}`}
              className="group"
              data-testid={`resource-card-${resource.id}`}
            >
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden card-hover h-full flex flex-col">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resource.image_url}
                    alt={resource.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 bg-[#1B3A5F] text-white text-xs font-medium rounded-full">
                      {resource.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="heading-font text-xl font-semibold text-[#1B3A5F] mb-3 group-hover:text-[#2C5282]">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 flex-1">
                    {resource.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <div className="flex items-center text-gray-500 text-sm">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{resource.read_time} min read</span>
                    </div>
                    <div className="flex items-center text-[#1B3A5F] font-medium group-hover:text-[#2C5282]">
                      <span className="text-sm mr-1">Read More</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredResources.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No resources found in this category</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#1B3A5F] text-white py-6 mt-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-white/80 text-sm">© 2026 Anchor Lane</p>
        </div>
      </footer>
    </div>
  );
}
